import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X,
  Users,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Check,
  Timer,
  Flag
} from 'lucide-react';
import { useApi } from '../../contexts/ApiContext';

export default function AssignToRaceModal({ isOpen, onClose, eventId, entries, onAssignComplete }) {
  const api = useApi();
  const [selectedRace, setSelectedRace] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [result, setResult] = useState(null);

  const { data: races = [], isLoading: loading } = useQuery({
    queryKey: ['assignable-races', eventId],
    queryFn: async () => {
      setSelectedEntries(entries.map(e => e.id));
      setResult(null);
      const response = await api.events.getRaces(eventId);
      if (!response.success) return [];
      return (response.data || []).filter(race => {
        const raceType = (race.race_type || '').toLowerCase();
        return raceType.includes('hot lap') ||
               raceType.includes('qualifying') ||
               raceType.includes('qual') ||
               raceType.includes('time trial');
      });
    },
    enabled: isOpen && !!eventId,
  });

  const handleToggleEntry = (entryId) => {
    setSelectedEntries(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEntries.length === entries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(entries.map(e => e.id));
    }
  };

  const handleAssign = async () => {
    if (!selectedRace || selectedEntries.length === 0) return;

    setAssigning(true);
    setResult(null);

    try {
      // Get entrant IDs from entries
      const entrantIds = entries
        .filter(e => selectedEntries.includes(e.id))
        .map(e => e.id);

      const response = await api.races.bulkAddToLineup(selectedRace.race_ID, entrantIds);

      if (response.success) {
        setResult({
          type: 'success',
          message: response.data?.message || `Added ${response.data?.added || 0} drivers to ${selectedRace.race_name}`
        });
        if (onAssignComplete) {
          onAssignComplete(selectedRace, response.data);
        }
      } else {
        setResult({
          type: 'error',
          message: response.message || 'Failed to assign drivers'
        });
      }
    } catch (err) {
      console.error('Error assigning drivers:', err);
      setResult({
        type: 'error',
        message: 'Failed to assign drivers to race'
      });
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  const getRaceIcon = (raceType) => {
    const type = (raceType || '').toLowerCase();
    if (type.includes('hot lap')) return Timer;
    if (type.includes('qualifying') || type.includes('qual')) return Flag;
    return Users;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative bg-white rounded-lg border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-gradient-to-r from-slate-800 to-slate-700">
          <div>
            <h3 className="text-lg font-semibold text-white">Assign to Race</h3>
            <p className="text-sm text-slate-300">
              Select a hot laps or qualifying session
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-300 hover:text-white hover:bg-white/10 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Result Message */}
          {result && (
            <div className={`mb-4 p-3 rounded flex items-center gap-2 ${
              result.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {result.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
              )}
              <span className="text-sm">{result.message}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            </div>
          ) : races.length === 0 ? (
            <div className="text-center py-8">
              <Timer className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h4 className="font-medium text-slate-700 mb-1">No Assignable Races</h4>
              <p className="text-sm text-slate-500">
                Create Hot Laps or Qualifying races first on the Schedule of Events page.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Race Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Race
                </label>
                <div className="grid grid-cols-1 gap-2">
                  {races.map(race => {
                    const Icon = getRaceIcon(race.race_type);
                    const isSelected = selectedRace?.race_ID === race.race_ID;

                    return (
                      <button
                        key={race.race_ID}
                        onClick={() => setSelectedRace(race)}
                        className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                          isSelected
                            ? 'border-wrg-blue-600 bg-wrg-blue-50 ring-1 ring-wrg-blue-600'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-wrg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate">
                            {race.race_name}
                          </div>
                          <div className="text-xs text-slate-500">
                            {race.race_type} - {race.entry_count || 0} already assigned
                          </div>
                        </div>
                        {isSelected && (
                          <Check className="w-5 h-5 text-wrg-blue-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Entry Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Entries to Assign ({selectedEntries.length} of {entries.length})
                  </label>
                  <button
                    onClick={handleSelectAll}
                    className="text-xs text-wrg-blue-600 hover:text-wrg-blue-700"
                  >
                    {selectedEntries.length === entries.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                  {entries.map(entry => (
                    <label
                      key={entry.id}
                      className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedEntries.includes(entry.id)}
                        onChange={() => handleToggleEntry(entry.id)}
                        className="w-4 h-4 text-wrg-blue-600 border-slate-300 rounded focus:ring-wrg-blue-500"
                      />
                      <span className="font-mono font-bold text-sm text-slate-800 w-12">
                        {entry.car_number || '-'}
                      </span>
                      <span className="text-sm text-slate-700 flex-1 truncate">
                        {entry.driver_name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-4 py-3 bg-slate-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
          >
            {result?.type === 'success' ? 'Done' : 'Cancel'}
          </button>
          <button
            onClick={handleAssign}
            disabled={assigning || !selectedRace || selectedEntries.length === 0 || races.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-wrg-blue-600 text-white text-sm rounded hover:bg-wrg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
          >
            {assigning ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Assigning...
              </>
            ) : (
              <>
                <Users className="w-4 h-4" />
                Assign {selectedEntries.length} Driver{selectedEntries.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
